/**
 * Registry → AI models panel: gateway status banner, the model table, and
 * the create/edit dialog, wired to `useAiModels`.
 */

import { useState, type ReactElement } from 'react';
import type { AiModel, AiModelCreate } from '@services/aiModelsService';
import { useAiModels, useGatewayStatus, useCreateAiModel, useUpdateAiModel } from '../hooks/useAiModels';
import { GatewayStatusBanner } from './GatewayStatusBanner';
import { AiModelTable } from './AiModelTable';
import { AiModelFormDialog } from './AiModelFormDialog';
import { extractErrorDetail } from '../utils/errorDetail';

export function AiModelsPanel(): ReactElement {
  const { data: models, isLoading, error } = useAiModels();
  const { data: status } = useGatewayStatus();
  const createModel = useCreateAiModel();
  const updateModel = useUpdateAiModel();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState<AiModel | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const handleCreate = (): void => {
    setSelectedModel(null);
    setFormError(null);
    setDialogOpen(true);
  };

  const handleEdit = (model: AiModel): void => {
    setSelectedModel(model);
    setFormError(null);
    setDialogOpen(true);
  };

  const handleSubmit = (body: Partial<AiModelCreate>): void => {
    setFormError(null);
    const onSuccess = (): void => { setDialogOpen(false); };
    const onError = (err: unknown): void => { setFormError(extractErrorDetail(err)); };
    if (selectedModel) {
      updateModel.mutate(
        { modelId: selectedModel.model_id, body },
        { onSuccess, onError },
      );
    } else {
      createModel.mutate(body, { onSuccess, onError });
    }
  };

  if (isLoading) {
    return <p>Loading AI models...</p>;
  }

  if (error) {
    return (
      <p role="alert">Failed to load the AI model registry. Please refresh the page.</p>
    );
  }

  return (
    <>
      <GatewayStatusBanner status={status} />

      <div className="admin-page__header">
        <button type="button" className="admin-page__create-btn" onClick={handleCreate}>
          Register Model
        </button>
      </div>

      <AiModelTable models={models ?? []} onEdit={handleEdit} />

      <AiModelFormDialog
        open={dialogOpen}
        model={selectedModel}
        onClose={() => { setDialogOpen(false); }}
        onSubmit={handleSubmit}
        isSubmitting={createModel.isPending || updateModel.isPending}
        submitError={formError}
      />
    </>
  );
}
